import { IPelicanSectionSettings, copyTradeAPIController } from '@aqx-core/common/api/copy-trade';
import { ECopyTradeFilter } from '@aqx-core/common/constants/copy-trade';
import { companyService } from '@aqx-core/common/services/company';
import { copyTradeViewController } from '@aqx-core/common/services/copy-trade/controller';
import { copyTradeService } from '@aqx-core/common/services/copy-trade/service';
import { validateCompanyAccess } from '@aqx-core/common/utils/company-access';

import { copyTradeSectionSettingsService } from './service';
import {
  IEditAvailableStrategySectionsParams,
  IGetAllStrategySectionsParams,
  IGetAvailableStrategySectionsParams
} from './types';

class CopyTradeSectionSettingsViewController {
  public getFallbackSection = (): ECopyTradeFilter => {
    const companyProfile = companyService.context?.companyProfile;
    const { hasPremiumCopyTradeAccess } = validateCompanyAccess(companyProfile);
    const availableSections = copyTradeSectionSettingsService.context.availableStrategySections;

    if (hasPremiumCopyTradeAccess) {
      return ECopyTradeFilter.ALL;
    }

    if (availableSections.length > 0 && !availableSections.includes(ECopyTradeFilter.ALL)) {
      return ECopyTradeFilter.TOP_FREE_SIGNALS;
    }

    return ECopyTradeFilter.ALL;
  };

  public getAvailableStrategySections = async ({ onSuccess, onError }: IGetAvailableStrategySectionsParams) => {
    copyTradeSectionSettingsService.setStrategySectionsLoading(true);

    await copyTradeAPIController.getAvailableStrategySections({
      onSuccess: (result) => {
        copyTradeSectionSettingsService.setAvailableStrategySections(result ?? []);
        onSuccess?.();
        copyTradeSectionSettingsService.setStrategySectionsLoading(false);
      },
      onError: () => {
        onError?.();
        copyTradeSectionSettingsService.setStrategySectionsLoading(false);
      }
    });
  };

  public getAllStrategySections = async ({ onSuccess, onError }: IGetAllStrategySectionsParams) => {
    copyTradeSectionSettingsService.setStrategySectionsLoading(true);
    await copyTradeAPIController.getAllStrategySections({
      onSuccess: (result) => {
        onSuccess?.();
        copyTradeSectionSettingsService.setAllStrategySections(result ?? []);
        copyTradeSectionSettingsService.setStrategySectionsLoading(false);
      },
      onError: () => {
        onError?.();
        copyTradeSectionSettingsService.setStrategySectionsLoading(false);
      }
    });
  };

  public initializeEditingStrategySections = () => {
    const allSections = copyTradeSectionSettingsService.context.allStrategySections;
    const preferenceSections = copyTradeSectionSettingsService.context.availableStrategySections;

    if (allSections.length === 0) {
      return;
    }

    const allSectionsMap = new Map();
    allSections.forEach((section) => {
      allSectionsMap.set(section.label, section);
    });

    const orderedSections: IPelicanSectionSettings[] = [];

    preferenceSections.forEach((prefLabel) => {
      const section = allSectionsMap.get(prefLabel);
      if (section) {
        orderedSections.push({
          ...section,
          visible: true,
          order: orderedSections.length
        });
      }
    });

    allSections.forEach((section) => {
      if (!preferenceSections.includes(section.label)) {
        orderedSections.push({
          ...section,
          visible: false,
          order: orderedSections.length
        });
      }
    });

    copyTradeSectionSettingsService.setEditingStrategySections(orderedSections);
    copyTradeSectionSettingsService.setOriginalStrategySections([...orderedSections]);
  };

  public updateEditingStrategySections = (sections: IPelicanSectionSettings[]) => {
    copyTradeSectionSettingsService.setEditingStrategySections(sections);
  };

  public editAvailableStrategySections = async ({
    sections,
    onSuccess,
    onError
  }: IEditAvailableStrategySectionsParams) => {
    copyTradeSectionSettingsService.setStrategySectionsLoading(true);

    const visibleSections = sections
      .filter((section) => section.visible)
      .sort((a, b) => a.order - b.order)
      .map((section) => section.label);

    const currentSelectedSection = copyTradeService.context.selectedStrategySection;

    await copyTradeAPIController.editAvailableStrategySections(
      { sectionsPreferenceOrder: visibleSections },
      {
        onSuccess: async () => {
          onSuccess?.();
          await this.getAvailableStrategySections({});
          this.initializeEditingStrategySections();

          const updatedAvailableSections = copyTradeSectionSettingsService.context.availableStrategySections;
          if (!updatedAvailableSections.includes(currentSelectedSection)) {
            const fallbackSection = this.getFallbackSection();
            await copyTradeViewController.onStrategySectionSelected({ section: fallbackSection });
          }

          copyTradeSectionSettingsService.setStrategySectionsLoading(false);
        },
        onError: () => {
          onError?.();
          copyTradeSectionSettingsService.setStrategySectionsLoading(false);
        }
      }
    );
  };
}

export const copyTradeSectionSettingsViewController = new CopyTradeSectionSettingsViewController();
